import type { IUserKeywordGroup } from "app/services/keyword-groups/keywordGroups.types";

import type { IAiTopic, IAutocompleteKeywordGroup } from "../../AutocompleteKeywords/types/AutocompleteKeywordGroupTypes";

export type AutocompleteDemandAnalysisEntity = IUserKeywordGroup | IAiTopic;

export const AutocompleteDemandAnalysisEntityType = {
    ownList: "ownList",
    sharedList: "sharedList",
    aiTopic: "aiTopic",
} as const;

/**
 * Icon mapping for each entity type
 */
export const ENTITY_ICONS = {
    [AutocompleteDemandAnalysisEntityType.ownList]: "nav-keyword-group",
    [AutocompleteDemandAnalysisEntityType.sharedList]: "group-shared",
    [AutocompleteDemandAnalysisEntityType.aiTopic]: "search",
} as const;

export type AutocompleteDemandAnalysisEntityType =
    (typeof AutocompleteDemandAnalysisEntityType)[keyof typeof AutocompleteDemandAnalysisEntityType];

/**
 * Search relevance information for sorting within entity groups
 */
export interface ISearchRelevance {
    isExactMatch: boolean;
    startsWithQuery: boolean;
    matchIndex: number; // Position where match starts (-1 if no match)
}

interface IAutocompleteDemandAnalysisItem {
    id: string;
    name: string;
    type: AutocompleteDemandAnalysisEntityType;
    icon: (typeof ENTITY_ICONS)[AutocompleteDemandAnalysisEntityType];
    searchRelevance: ISearchRelevance;
    originalData: IAutocompleteKeywordGroup | IAiTopic;
}

interface IAutocompleteDemandAnalysisOwnListItem extends IAutocompleteDemandAnalysisItem {
    type: typeof AutocompleteDemandAnalysisEntityType.ownList;
    originalData: IAutocompleteKeywordGroup;
}

interface IAutocompleteDemandAnalysisSharedListItem extends IAutocompleteDemandAnalysisItem {
    type: typeof AutocompleteDemandAnalysisEntityType.sharedList;
    originalData: IAutocompleteKeywordGroup;
}

interface IAutocompleteDemandAnalysisAiTopicItem extends IAutocompleteDemandAnalysisItem {
    type: typeof AutocompleteDemandAnalysisEntityType.aiTopic;
    originalData: IAiTopic;
}

export type AiTopicSearchResult = {
    name: string;
};

/**
 * Union type for all possible unified items
 */
export type AutocompleteDemandAnalysisItem =
    | IAutocompleteDemandAnalysisOwnListItem
    | IAutocompleteDemandAnalysisSharedListItem
    | IAutocompleteDemandAnalysisAiTopicItem;

const isOwnListItem = (item: AutocompleteDemandAnalysisItem): item is IAutocompleteDemandAnalysisOwnListItem =>
    item.type === AutocompleteDemandAnalysisEntityType.ownList;

const isSharedListItem = (
    item: AutocompleteDemandAnalysisItem,
): item is IAutocompleteDemandAnalysisSharedListItem => item.type === AutocompleteDemandAnalysisEntityType.sharedList;

/**
 * Type predicate to check if an item is own list or shared list
 */
export const isKeywordGroupItem = (
    item: AutocompleteDemandAnalysisItem,
): item is IAutocompleteDemandAnalysisOwnListItem | IAutocompleteDemandAnalysisSharedListItem =>
    isOwnListItem(item) || isSharedListItem(item);
